import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'
import type {
  TableView,
  TableViewConfig,
  TableViewShareScope,
} from '@/admin/types/table-views'

/**
 * Fetch all views (personal + org defaults) for a given table.
 */
export function useTableViews(tableId: string) {
  return useQuery<TableView[]>({
    queryKey: ['table-views', tableId],
    queryFn: () =>
      api.get('/table-views/', { params: { table_id: tableId } }).then((r) => r.data),
    staleTime: 30_000,
    // Coerce to an array defensively. This query is persisted to IndexedDB
    // under the REFERENCE profile (60-min TTL, no refetch until expiry), so a
    // malformed or legacy-shaped cached value would otherwise rehydrate and
    // crash every consumer's `views.map(...)` for up to an hour. Fresh
    // responses are always an array (list endpoint), so coercing a bad shape
    // to [] is safe.
    select: (data) => (Array.isArray(data) ? data : []),
  })
}

/**
 * Create a personal table view.
 */
export function useCreateTableView() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      table_id: string
      name: string
      // List pages store a TableViewConfig; other surfaces (the dashboard) store
      // their own JSON shape under their own table_id.
      config: TableViewConfig | Record<string, unknown>
      position?: number
      share_scope?: TableViewShareScope
      /** User uuids, when share_scope is "people". */
      shared_with?: string[]
    }) =>
      api.post('/table-views/', data).then((r) => r.data as TableView),
    onSuccess: (data, variables) => {
      // Show the new view straight away; the refetch then confirms it.
      qc.setQueryData<TableView[]>(['table-views', variables.table_id], (old) =>
        Array.isArray(old) ? [...old, data] : old,
      )
      qc.invalidateQueries({ queryKey: ['table-views', variables.table_id] })
      toast.success('View saved')
    },
    onError: (err) => {
      toastApiError(err, 'Failed to save view')
    },
  })
}

/**
 * Update an existing table view.
 */
export function useUpdateTableView() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (variables: {
      uuid: string
      tableId: string
      name?: string
      config?: TableViewConfig | Record<string, unknown>
      position?: number
      share_scope?: TableViewShareScope
      shared_with?: string[]
    }) => {
      const { uuid, tableId: _unused, ...data } = variables
      void _unused
      return api.put(`/table-views/${uuid}/`, data).then((r) => r.data as TableView)
    },
    onSuccess: (data, variables) => {
      // Swap in the saved view at once, so anything comparing against it (a
      // dashboard's "unsaved changes" state) never sees the stale copy while
      // the refetch is in flight.
      qc.setQueryData<TableView[]>(['table-views', variables.tableId], (old) =>
        Array.isArray(old) ? old.map((v) => (v.uuid === data.uuid ? data : v)) : old,
      )
      qc.invalidateQueries({ queryKey: ['table-views', variables.tableId] })
    },
  })
}

/**
 * Soft-delete a table view.
 */
export function useDeleteTableView() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid }: { uuid: string; tableId: string }) =>
      api.delete(`/table-views/${uuid}/`),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['table-views', variables.tableId] })
    },
    onError: (err) => {
      toastApiError(err, 'Failed to delete view')
    },
  })
}

/**
 * Set the org-wide default view for a table.
 */
export function useSetOrgDefaultView() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { table_id: string; name: string; config: TableViewConfig }) =>
      api.post('/table-views/set-org-default/', data).then((r) => r.data as TableView),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['table-views', variables.table_id] })
    },
  })
}
